import React, {Component, Fragment} from 'react';
import axios from 'axios';
import classNames from 'classnames';
import {ships, ships2D, ship3D} from './settings';
import {scrollToElement, camelize} from '../../utils';
import {GlobalContextProvider, GlobalContext} from './context/GlobalContext';
import LoadViewer from './LoadViewer';
import Instructions from './Instructions';
import ThreeDShip from './ThreeDShip';
import TwoDShip from './TwoDShip';
import ChooseShipPanel from './ChooseShipPanel';

const
    hide2DShips = false,
    apiRootUrl = '/api/applicationarea/getareas',
    lubricantShipName = 'lubricant';

class ShipsViewerRoot extends Component {
    constructor(props) {
        super(props);

        this.aspectRatioSetterRef = React.createRef();

        // Links to images, icons and models added to Episerver
        this.shipsAssets = this.createShipsAssetsObject();

        this.state = {
            noShipCanBeShown: true,
            isDataLoaded: false,
            loadModels: false,
            isContainerFullHeight: false,
            errorsInData: {},
            applicationAreasData: {},
            activeShip: ''
        };

        this.handleExploreModelBtnClick = this.handleExploreModelBtnClick.bind(this);
        this.handleChooseShipBtnClick = this.handleChooseShipBtnClick.bind(this);
        this.handleCloseShipsViewerBtnClick = this.handleCloseShipsViewerBtnClick.bind(this);
        this.handleCloseShipsViewerEscape = this.handleCloseShipsViewerEscape.bind(this);
        this.handleTransitionEvent = this.handleTransitionEvent.bind(this);
    }

    componentDidMount() {
        for (const ship in ships) {
            if (ships.hasOwnProperty(ship)) {
                this.fetchShipData(ships[ship]);
            }
        }
        this.bindEventListeners();
        if (this.getFirstShipWithoutErrors() == lubricantShipName) {
            this.handleExploreModelBtnClick();
        }
       
    }

    componentWillUnmount() {
        this.unbindEventListeners();
    }

    bindEventListeners() {
        this.aspectRatioSetterRef.current.addEventListener('transitionend', this.handleTransitionEvent, false);
        window.addEventListener('keyup', this.handleCloseShipsViewerEscape, false);
    }

    unbindEventListeners() {
        this.aspectRatioSetterRef.current.removeEventListener('transitionend', this.handleTransitionEvent, false);
        window.removeEventListener('keyup', this.handleCloseShipsViewerEscape, false);
    }

    handleTransitionEvent(event) {
        if (event.propertyName === 'padding-top') {
            this.setState({isContainerFullHeight: !this.state.isContainerFullHeight});
        }
    }

    fetchShipData(ship) {
        const apiUrl = `${apiRootUrl}/${ship}`;

        axios.get(apiUrl)
            .then((response) => {
                const responseData = camelize(response.data);

                if (responseData.subareas) {
                    const applicationAreasData = {
                        ...this.state.applicationAreasData,
                        [ship]: responseData
                    };
                    const errorsInData = {
                        ...this.state.errorsInData,
                        [ship]: false
                    };

                    this.setState({
                        applicationAreasData,
                        errorsInData
                    }, this.checkIfAllDataIsLoaded);
                } else {
                    const errorsInData = {
                        ...this.state.errorsInData,
                        [ship]: true
                    };
                    this.setState({errorsInData});
                    console.error(`Subareas couldn't be found for ${ships[ship]}`);
                }
            })
            .catch((error) => {
                console.groupCollapsed(`Errors in: ${ships[ship]}`);
                console.info(`Couldn't load data for ${ships[ship]}`);
                console.error(error);
                console.groupEnd();

                const errorsInData = {
                    ...this.state.errorsInData,
                    [ship]: true
                };
                this.setState({errorsInData});
            });
    }

    checkIfAllDataIsLoaded() {
        const
            errorsInDataLength = Object.keys(this.state.errorsInData).length,
            shipsLength = Object.keys(ships).length;

        if (errorsInDataLength === shipsLength) {
            this.setState({
                isDataLoaded: true,
                noShipCanBeShown: this.checkIfNoShipCanBeShown(),
                activeShip: this.getFirstShipWithoutErrors()
            });
        }
    }

    getFirstShipWithoutErrors() {
        let shipWithoutErrors = '';
        for (let ship in ships) {
            if (ships.hasOwnProperty(ship) && !this.state.errorsInData[ship]
            && (this.shipsAssets[ship].modelUrl.trim() != "" ||this.shipsAssets[ship].imageUrl.trim() != "")) {
                shipWithoutErrors = ship;
                break;
            }
        }

        return shipWithoutErrors;
    }

    getShipAssets(shipName) {
        let shipAssets = {};
        this.props.shipsAssetsRaw.forEach((item) => {
                if (item.id.indexOf(shipName) >= 0) {
                    shipAssets = item.dataset;
                }
            }
        );
        return shipAssets;
    }

    createShipsAssetsObject() {
        const shipsAssets = {};
        for (let ship in ships) {
            if (ships.hasOwnProperty(ship)) {
                shipsAssets[ship] = this.getShipAssets(ship);
            }
        }
        return shipsAssets;
    }

    handleExploreModelBtnClick() {
        this.setState({loadModels: true});
        scrollToElement(this.aspectRatioSetterRef.current);
    }

    handleChooseShipBtnClick(chosenShip) {
        this.setState({activeShip: chosenShip});
    }

    handleCloseShipsViewerBtnClick(event) {
        event.preventDefault();
        this.props.setCategoriesListVisibility(false);
        this.setState({loadModels: false,});
    }

    handleCloseShipsViewerEscape(event) {
        event.preventDefault();
        const escapeKeyCode = 27;

        if (event.keyCode === escapeKeyCode && this.state.loadModels) {
            this.props.setCategoriesListVisibility(false);
            this.setState({loadModels: false});
        }
    }

    checkIfNoShipCanBeShown() {
        // Don't show ships viewer section if errors occur in all ships
        const {errorsInData} = this.state;
        let cannotBeShown = true;

        for (let ship in errorsInData) {
            if (errorsInData.hasOwnProperty(ship) && !errorsInData[ship]) {
                cannotBeShown = false;
            }
        }

        // Don't show ships viewer section if errors in bulk
        if (hide2DShips && errorsInData[ships.bulk]) {
            cannotBeShown = true;
        }
        return cannotBeShown;
    }

    generateShips(modelType) {
        const {
            applicationAreasData,
            activeShip,
            errorsInData
        } = this.state;

        let shipComponents = null;

        if (modelType === '2D') {
            shipComponents = ships2D.map((ship) => {
                if (!errorsInData[ship]) {
                    return (
                        <TwoDShip
                            applicationAreas={applicationAreasData[ship].subareas}
                            shipAssets={this.shipsAssets[ship]}
                            activeShip={activeShip}
                            ship={ship}
                            key={ship}
                        />
                    )
                }
            })
        } else if (modelType === '3D') {
            shipComponents = ship3D.map((ship) => {
                if (!errorsInData[ship]) {
                    return (
                        <ThreeDShip
                            applicationAreas={applicationAreasData[ship].subareas}
                            shipAssets={this.shipsAssets[ship]}
                            activeShip={activeShip}
                            ship={ship}
                            key={ship}
                        />
                    )
                }
            })
        }

        return shipComponents;
    }

    renderShips() {
        const {
            loadModels,
            isContainerFullHeight,
            isDataLoaded,
            applicationAreasData,
            activeShip,
            errorsInData
        } = this.state;

        let shipsComponents = null,
            isLubricantShip = this.state.activeShip === lubricantShipName;

        if (loadModels && (isContainerFullHeight || isLubricantShip ) && isDataLoaded) {
            const btnClassNames = `close-btn${isLubricantShip? ' btn-hide' : ''}`
            shipsComponents = (
                <Fragment>
                    <button
                        className={btnClassNames}
                        onClick={this.handleCloseShipsViewerBtnClick}
                    />
                    {this.generateShips('3D')}
                    {!hide2DShips &&
                    <Fragment>
                        {this.generateShips('2D')}
                        <ChooseShipPanel
                            errorsInData={errorsInData}
                            activeShip={activeShip}
                            applicationAreas={applicationAreasData}
                            shipsAssets={this.shipsAssets}
                            handleClick={this.handleChooseShipBtnClick}
                        />
                    </Fragment>
                    }
                </Fragment>
            )
        }

        return shipsComponents;
    }

    render() {
        const sectionStyles = this.state.noShipCanBeShown ? {display: 'none'} : {},
            shouldInstructionsBeRender = this.state.loadModels && this.state.isContainerFullHeight,
            isLubricantShip = this.state.activeShip === lubricantShipName;
        const containerClass = classNames(
            'ships-viewer__aspect-ratio-setter',
            `${isLubricantShip? 'ships-viewer__custom-bg-color' : ''}`,
            {
                'ships-viewer__not-active-overlay': this.state.loadModels,
                'ships-viewer__active-overlay': !this.state.loadModels
            }
        );
       
        return (
            <section className="row ships-viewer" style={sectionStyles}>
                {isLubricantShip && (
                    <div className="ships-viewer__heading">
                        <h2 className="ships-viewer__heading-title">Check out our lubricants offer</h2>
                        <p className="ships-viewer__heading-description">Click on the spot and see the products by application area</p>
                    </div>
                )}
                <div className={containerClass}
                     ref={this.aspectRatioSetterRef}
                >
                    {!isLubricantShip && <LoadViewer handleClick={this.handleExploreModelBtnClick}/>}
                    {this.renderShips()}
                    {!isLubricantShip && shouldInstructionsBeRender && <Instructions/>}
                </div>
            </section>
        );
    }
}

function ShipsViewerRootWithContext(props) {
    return (
        <GlobalContextProvider>
            <GlobalContext.Consumer>
                {(context) =>
                    <ShipsViewerRoot
                        setCategoriesListVisibility={context.setCategoriesListVisibility}
                        {...props}
                    />
                }
            </GlobalContext.Consumer>
        </GlobalContextProvider>
    )
}

export default ShipsViewerRootWithContext;